<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="smktariffcode-main">
    <Menus :menu="menus" @menuClicked="handleMenuClick" />
    <div class="right-container">
      <div class="top-section" v-if="hasSubscribed">
        <div class="filter-description">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
        >
          <div class="filter-popup">
            <MultiSelect
              :items="tariffTypeList"
              :selectedObject="selectedTariffTypes"
              itemKey="Tariff Description"
              placeholder="Tariff Types Selected"
              :tableBoxHideColumns="filterHideColumn"
              :checklist="true"
              :errorMessage="tariffTypesErrMsg"
              @update:selectedObject="updateTariffTypes"
            />
            <button @click="refreshClicked" :disabled="!tariffTypeList"> Refresh </button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <TableBox
        title="SMK Tariff Codes"
        :tableData.sync="tariffCodeSummaryData"
        :selectedRow.sync="selectedTariffCode"
        :loading="tableLoading"
        :columnOrderViews.sync="tableBoxColumnOrderViews"
        :columnOrderID="tableBoxColumnOrderID"
        :hideColumns="hideColumns"
        style="padding: 5px; border-radius: 5px"
      />
    </div>
    <PopupEntry
      v-if="popupEntryShow"
      :show.sync="popupEntryShow"
      :title="popUpTitle"
      :viewMode="viewMode"
      :successMessages="successMessages"
      @save="saveClick"
    >
      <div class="popup-entry-container">
        <div class="popup-top-section">
          <div style="display: flex; flex-direction: row; gap: 8px">
            <Input
              style="width: 92px"
              placeholder="Tariff Type"
              :inputValue.sync="selectedTariffType"
              v-if="hasSubscribed"
              :isDisabled="true"
            />
            <Input
              style="width: 172px"
              :errorMessage.sync="tcNumberErr"
              :isRequired="true"
              placeholder="TC Number"
              :inputValue.sync="selectedTCNumber"
              :isDisabled="popUpAction !== 'N'"
            />
            <Dropdown
              placeholder="Tariff Code Unit"
              :selectedObject.sync="selectedTCUnit"
              :isDisabled="viewMode"
              :search.sync="selectedTCUnitText"
              :allowFreeText.sync="allowFreeText"
              :items="unitOfQtyData"
              itemKey="UnitCode"
              style="width: 15%"
            />
          </div>
          <Input
            :errorMessage.sync="tcDescErr"
            :isRequired="true"
            placeholder="TC Description"
            :inputValue.sync="selectedTCDesc"
            :isDisabled="popUpAction !== 'N'"
          />
        </div>

        <div v-if="loadFormBody" class="smktariffcode-body">
          <div class="smktariffcode-subbody">
            <BoxContainer title="Import Duty" class="boxcontainer-width">
              <div class="smktariffcode-field">
                <p class="label-text">Normal:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpDutyAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpDutyType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedImpDutyQualifier"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Comparison [1] + [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpCA1Amt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpCA1Type"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpCA2Amt"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpCA2Type"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                  style="flex: 1 0 18%"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Composite [1] + [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpCO1Amt"
                  class="input-amount"
                  validator="positiveDecimal,3,3"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpCO1Type"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpCO2Amt"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpCO2Type"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                  style="flex: 1 0 18%"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">ASEAN CEPT [1]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedASEANCEPTAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedASEANCEPTType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />

                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedASEANCEPTQualifier"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">ASEAN CEPT [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedASEANCEPTAmt2"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedASEANCEPTType2"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />

                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedASEANCEPTQualifier2"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Operator:</p>
                <Dropdown
                  placeholder="Operator"
                  :selectedObject.sync="selectedASEANCEPTOperator"
                  :isDisabled="viewMode"
                  :items="selectedOperatorList"
                  itemKey="value"
                />
              </div>

              <div class="smktariffcode-field" v-if="!hasSubscribed">
                <p class="label-text">ASEAN PTA:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedImpASEANPTAAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedImpASEANPTAType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedImpASEANPTAQualifier"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>
            </BoxContainer>
            <BoxContainer title="Export Duty" class="boxcontainer-width">
              <div class="smktariffcode-field">
                <p class="label-text">Normal [1]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedExpDutyAmt"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedExpDutyType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedExpDutyQualifier"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Normal [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedExpDutyAmt2"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedExpDutyType2"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedExpDutyQualifier2"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>
            </BoxContainer>
            <BoxContainer title="Excise" class="boxcontainer-width">
              <div class="smktariffcode-field">
                <p class="label-text">Alcohol Qualifier:</p>
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedAlcoholQualifier"
                  :isDisabled="viewMode"
                  :items="[unitOfQtyData[23]]"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>
              <div class="smktariffcode-field">
                <p class="label-text">Excise [1]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedExciseAmt1"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedExciseType1"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Excise [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedExciseAmt2"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedExciseType2"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedExciseQualifier2"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Operator:</p>
                <Dropdown
                  placeholder="Operator"
                  :selectedObject.sync="selectedExciseOperator"
                  :isDisabled="viewMode"
                  :items="selectedOperatorList"
                  itemKey="value"
                />
              </div>
            </BoxContainer>
          </div>

          <div class="smktariffcode-subbody">
            <BoxContainer title="Sales Tax" class="boxcontainer-width">
              <div class="smktariffcode-field">
                <p class="label-text">Sales Tax:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedTaxDutyAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedTaxDutyType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedTaxDutyQualifier"
                  :isDisabled="viewMode"
                  :items="[unitOfQtyData[4]]"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>
            </BoxContainer>
            <BoxContainer title="CESS" class="boxcontainer-width">
              <div class="smktariffcode-field">
                <p class="label-text">CESS [Planting]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedCESSForPlantingAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Input placeholder="Type" inputValue="$" :isDisabled="true" class="field-type" />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">CESS [Research]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedCESSForResearchAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Input placeholder="Type" inputValue="$" :isDisabled="true" class="field-type" />
              </div>
              <div class="smktariffcode-field">
                <p class="label-text">CESS Rate:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedCESSRate"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,2,5"
                  class="input-amount"
                />
                <Input placeholder="Type" inputValue="%" :isDisabled="true" class="field-type" />
              </div>
            </BoxContainer>
            <BoxContainer title="Anti Dumping" class="boxcontainer-width" v-if="!hasSubscribed">
              <div class="smktariffcode-field">
                <p class="label-text">AD [1]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedAntiDumpingAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedAntiDumpingType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">AD [2]:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedAntiDumpingAmt2"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedAntiDumpingType2"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedAntiDumpingQualifier2"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Operator:</p>
                <Dropdown
                  placeholder="Operator"
                  :selectedObject.sync="selectedAntiDumpingOperator"
                  :isDisabled="viewMode"
                  :items="selectedOperatorList"
                  itemKey="value"
                />
              </div>
              <div class="smktariffcode-field">
                <p class="label-text">ASEAN PTA:</p>
                <Input
                  placeholder="Amt"
                  :inputValue.sync="selectedADASEANPTAAmt"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,3,3"
                  class="input-amount"
                />
                <Dropdown
                  placeholder="Type"
                  :selectedObject.sync="selectedADASEANPTAType"
                  :isDisabled="viewMode"
                  :items="selectedTypeList"
                  itemKey="value"
                  validator="positiveDecimal,3"
                  class="field-type"
                />
                <Dropdown
                  placeholder="Unit"
                  :selectedObject.sync="selectedADASEANPTAQualifier"
                  :isDisabled="viewMode"
                  :items="unitOfQtyData"
                  itemKey="UnitCode"
                  validator="text,3"
                  class="field-qualifier"
                />
              </div>
            </BoxContainer>
            <BoxContainer title="Trade Controls" class="boxcontainer-width" v-if="hasSubscribed">
              <div class="smktariffcode-field">
                <p class="label-text">TRQ:</p>
                <Input
                  placeholder="TRQ Value"
                  :inputValue.sync="selectedTRQ"
                  :isDisabled="viewMode"
                  style="flex: 1 1 0; min-width: 0"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Import Prohibition:</p>
                <Input
                  placeholder="Imp Pro Value"
                  :inputValue.sync="selectedImpProhibition"
                  :isDisabled="viewMode"
                  style="flex: 1 1 0; min-width: 0"
                />
              </div>

              <div class="smktariffcode-field">
                <p class="label-text">Export Prohibition:</p>
                <Input
                  placeholder="Exp Pro Value"
                  :inputValue.sync="selectedExpProhibition"
                  :isDisabled="viewMode"
                  style="flex: 1 1 0; min-width: 0"
                />
              </div>
            </BoxContainer>
          </div>
        </div>
        <div v-if="loadCompareBody" class="smktariffcode-body"> </div>
        <div v-if="loadingScreen" class="smktariffcode-body-wait">
          <img src="https://dsmlib.logistics.systems/Icons/Loading.gif" width="100" />
          Loading...
        </div>
      </div>
    </PopupEntry>
  </div>
</template>

<script>
import MultiSelect from '@/components/v2testComponent/MultiSelect.vue';
export default {
  components: {
    MultiSelect,
  },
  computed: {
    isPDK2025() {
      return this.selectedTariffType === 'PDK2025';
    },

    excise2Type: {
      get() {
        return this.popUpAction === 'N' ? this.selectedTypeList[1] : this.selectedExciseType2;
      },
      set(value) {
        if (this.popUpAction !== 'N') {
          this.selectedExciseType2 = value;
        }
      },
    },

    excise1Type: {
      get() {
        return this.popUpAction === 'N' ? this.selectedTypeList[1] : this.selectedExciseType1;
      },
      set(value) {
        if (this.popUpAction !== 'N') {
          this.selectedExciseType1 = value;
        }
      },
    },
  },
  data: function () {
    return {
      // Summary table
      tariffCodeSummaryData: [],
      selectedTariffCode: [],
      tableLoading: true,
      tableBoxColumnOrderViews: [],
      tableBoxColumnOrderID: null,
      columnOrderViewsByReportName: {},
      hideColumns: [],

      //Filter Table
      filterText: '',
      mainFilterIsOpen: false,
      tariffTypeList: [],
      selectedTariffTypes: [],
      lastRefreshed: '',
      disableFilterButton: true,
      filterHideColumn: [],
      tariffTypesErrMsg: '',

      //Popup
      viewMode: false,
      popUpTitle: '',
      popUpAction: '',
      popupEntryShow: false,
      successMessages: [],

      //Compare Popup table
      loadCompareBody: false,

      //Popup table
      reloadTable: false,
      loadingScreen: false,
      loadFormBody: false,
      selectedTCUnit: null,
      allowFreeText: false,
      selectedTCUnitText: '',
      selectedTCNumber: null,
      tcNumberErr: '',
      selectedTCDesc: null,
      tcDescErr: '',
      selectedTariffType: null,
      selectedImpDutyAmt: null,
      selectedImpDutyType: null,
      selectedImpDutyQualifier: null,
      selectedImpCA1Amt: null,
      selectedImpCA1Type: null,
      selectedImpCA2Amt: null,
      selectedImpCA2Type: null,
      selectedImpCO1Amt: null,
      selectedImpCO1Type: null,
      selectedImpCO2Amt: null,
      selectedImpCO2Type: null,
      selectedImpASEANPTAAmt: null,
      selectedImpASEANPTAType: null,
      selectedImpASEANPTAQualifier: null,
      selectedExpDutyAmt: null,
      selectedExpDutyType: null,
      selectedExpDutyQualifier: null,
      selectedExpDutyAmt2: null,
      selectedExpDutyType2: null,
      selectedExpDutyQualifier2: null,
      selectedTaxDutyAmt: null,
      selectedTaxDutyType: null,
      selectedTaxDutyQualifier: null,
      selectedASEANCEPTAmt: null,
      selectedASEANCEPTType: null,
      selectedASEANCEPTQualifier: null,
      selectedASEANCEPTAmt2: null,
      selectedASEANCEPTType2: null,
      selectedASEANCEPTQualifier2: null,
      selectedASEANCEPTOperator: null,
      selectedCESSForPlantingAmt: null,
      selectedCESSForResearchAmt: null,
      selectedCESSRate: null,
      selectedExciseAmt2: null,
      selectedExciseType2: null,
      selectedExciseOperator: null,
      selectedExciseAmt1: null,
      selectedExciseType1: null,
      selectedExciseQualifier2: null,
      selectedAlcoholQualifier: null,
      selectedAntiDumpingAmt: null,
      selectedAntiDumpingType: null,
      selectedAntiDumpingAmt2: null,
      selectedAntiDumpingType2: null,
      selectedTRQ: null,
      selectedImpProhibition: null,
      selectedExpProhibition: null,
      selectedAntiDumpingQualifier2: null,
      selectedAntiDumpingOperator: null,
      selectedADASEANPTAAmt: null,
      selectedADASEANPTAType: null,
      selectedADASEANPTAQualifier: null,
      selectedTypeList: [{ value: '%' }, { value: '$' }],
      selectedOperatorList: [{ value: 'AND' }, { value: 'OR' }],
      unitOfQtyData: null,

      //checkSubscription
      hasSubscribed: false,

      //Menu
      menus: [
        {
          title: 'Create SMK Tariff Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit SMK Tariff Code',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete SMK Tariff Code',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        },
        {
          title: 'View SMK Tariff Code',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          key: '',
          visible: true,
          enabled: false,
        },
        // {
        //   title: 'Compare SMK Tariff Codes',
        //   icon: '',
        //   key: '',
        //   visible: true,
        //   enabled: false,
        // },
      ],
    };
  },
  methods: {
    handleMenuClick: async function (menu) {
      this.loadCompareBody = false;
      this.loadFormBody = false;
      this.mainFilterIsOpen = false;
      switch (menu.title) {
        case 'Create SMK Tariff Code': {
          this.viewMode = false;
          this.popUpTitle = 'Create SMK Tariff Code';
          this.popUpAction = 'N';
          this.popupEntryShow = true;
          await this.fillPopUpTopSection();
          break;
        }
        case 'View SMK Tariff Code': {
          if (this.selectedTariffCode.length == 0) {
            this.$showAlert('error', 'No Tariff Code Selected', 'Please select a tariff code');
            return;
          }
          this.viewMode = true;
          this.popUpTitle = 'View SMK Tariff Code';
          this.popUpAction = 'V';
          this.popupEntryShow = true;
          await this.fillPopUpTopSection();
          break;
        }
        case 'Edit SMK Tariff Code': {
          if (this.selectedTariffCode.length == 0) {
            this.$showAlert('error', 'No Tariff Code Selected', 'Please select a tariff code');
            return;
          }
          this.popUpTitle = 'Edit SMK Tariff Code';
          this.popUpAction = 'E';
          this.popupEntryShow = true;
          this.viewMode = true; //Set to false once data is loaded
          await this.fillPopUpTopSection();
          break;
        }

        case 'Delete SMK Tariff Code': {
          if (this.selectedTariffCode.length == 0) {
            this.$showAlert('error', 'No Tariff Code Selected', 'Please select a tariff code');
            return;
          }
          this.popUpAction = 'D';
          const deleting = await this.$showAlert(
            'warning',
            'Delete SMK Tariff Code',
            `Are you sure you want to delete this Tariff Code?`,
            [
              { text: 'Delete', textColor: 'white', color: 'red' },
              { text: 'Cancel', textColor: 'black', color: 'lightgray' },
            ],
          );

          if (deleting == 'Cancel') {
            return;
          }

          await this.$apiCallV2('/SMKTariffCode/SaveSMKTariffCode', {
            tcnumber: this.selectedTariffCode[0]['TcNumber'],
            actionflag: this.popUpAction,
          });
          this.reloadTableFunc();
          break;
        }
      }
    },
    async columnOrderViews() {
      this.tableBoxColumnOrderID = null;
      this.tableBoxColumnOrderViews = null;
      this.mainFilterIsOpen = false;
      const formctrlname = this.hasSubscribed ? 'SMKTariffCode' : 'SMKTariffCode_Master';
      const columnOrderID = {
        formname: 'CommonMaster_SMKTARIFFCODE',
        formctrlname: formctrlname,
      };

      if (!this.columnOrderViewsByReportName[formctrlname]) {
        this.columnOrderViewsByReportName[formctrlname] = await this.$apiCallV2(
          '/TableBox/GetColumnOrder',
          columnOrderID,
        );
      }

      this.$nextTick(() => {
        this.tableBoxColumnOrderViews = this.columnOrderViewsByReportName[formctrlname];
        this.tableBoxColumnOrderID = columnOrderID;
      });
    },
    reloadTableFunc() {
      this.reloadTable = false;
      this.$nextTick(() => {
        this.reloadTable = true;
      });
    },
    async handleLoading(bodyToLoad, apiToCall) {
      this[bodyToLoad] = false;
      this.loadingScreen = true;
      await apiToCall;
      this[bodyToLoad] = true;
      this.loadingScreen = false;
      return apiToCall;
    },
    async fillPopUpTopSection() {
      this.selectedTCNumber = null;
      this.selectedTCDesc = null;
      this.selectedTariffType = null;
      this.selectedTCUnit = null;
      this.selectedTCUnitText = '';
      this.resetFormFields();
      if (this.popUpAction !== 'N') {
        if (this.hasSubscribed) {
          this.selectedTariffType = this.selectedTariffCode[0]['Tariff Type'];
        }
        this.selectedTCNumber = this.selectedTariffCode[0]['Tariff Number'];
        this.selectedTCDesc = this.selectedTariffCode[0]['Description'];
        await this.fillPopupBodySection();
      } else {
        this.loadFormBody = true;
      }
    },
    async fillPopupBodySection() {
      const jsonBody = {
        tcnumber: this.selectedTCNumber,
        tarifftype: this.selectedTariffType,
      };
      const details = (
        await this.handleLoading(
          'loadFormBody',
          this.hasSubscribed ?
            this.$apiCallV2('/SMKTariffCode/GetSMKTariffCodeMaster', jsonBody)
          : this.$apiCallV2('/SMKTariffCode/GetSMKTariffCodeSummaryDetails', jsonBody),
        )
      )[0];

      if (this.popUpAction === 'E') this.viewMode = false;

      this.selectedTCUnit = this.unitOfQtyData.getSelected(details['Tariff Unit'], 'UnitCode');

      if (this.hasSubscribed && this.popUpAction === 'V') {
        this.selectedTCUnitText = details['Tariff Unit'];
      }

      this.selectedImpDutyAmt = details['Import Duty Rate'];
      this.selectedImpDutyType = this.selectedTypeList.getSelected(details['Import Duty Type'], 'value');
      this.selectedImpDutyQualifier = this.unitOfQtyData.getSelected(details['Import Duty Qualifier'], 'UnitCode');

      this.selectedImpCA1Amt = details['Import Comparison 1 Rate'];
      this.selectedImpCA1Type = this.selectedTypeList.getSelected(details['Import Comparison 1 Type'], 'value');
      this.selectedImpCA2Type = this.selectedTypeList.getSelected(details['Import Comparison 2 Type'], 'value');
      this.selectedImpCA2Amt = details['Import Comparison 2 Rate'];

      this.selectedImpCO1Amt = details['Import Composite 1 Rate'];
      this.selectedImpCO1Type = this.selectedTypeList.getSelected(details['Import Composite 1 Type'], 'value');
      this.selectedImpCO2Amt = details['Import Composite 2 Rate'];
      this.selectedImpCO2Type = this.selectedTypeList.getSelected(details['Import Composite 2 Type'], 'value');

      this.selectedExpDutyAmt = details['Export Duty Rate 1'];
      this.selectedExpDutyType = this.selectedTypeList.getSelected(details['Export Duty Type 1'], 'value');
      this.selectedExpDutyQualifier = this.unitOfQtyData.getSelected(details['Export Duty Qualifier 1'], 'UnitCode');

      this.selectedExpDutyAmt2 = details['Export Duty Rate 2'];
      this.selectedExpDutyType2 = this.selectedTypeList.getSelected(details['Export Duty Type 2'], 'value');
      this.selectedExpDutyQualifier2 = this.unitOfQtyData.getSelected(details['Export Duty Qualifier 2'], 'UnitCode');

      this.selectedTaxDutyAmt = details['Tax Rate'];
      this.selectedTaxDutyType = this.selectedTypeList.getSelected(details['Tax Type'], 'value');
      this.selectedTaxDutyQualifier = this.unitOfQtyData.getSelected(details['Tax Qualifier'], 'UnitCode');

      this.selectedASEANCEPTAmt = details['ASEAN CEPT Duty Rate'];
      this.selectedASEANCEPTType = this.selectedTypeList.getSelected(details['ASEAN CEPT Duty Type'], 'value');
      this.selectedASEANCEPTQualifier = this.unitOfQtyData.getSelected(
        details['ASEAN CEPT Duty Qualifier'],
        'UnitCode',
      );

      this.selectedASEANCEPTAmt2 = details['ASEAN CEPT Duty Rate 2'];
      this.selectedASEANCEPTType2 = this.selectedTypeList.getSelected(details['ASEAN CEPT Duty Type 2'], 'value');
      this.selectedASEANCEPTQualifier2 = this.unitOfQtyData.getSelected(
        details['ASEAN CEPT Duty Qualifier 2'],
        'UnitCode',
      );
      this.selectedASEANCEPTOperator = this.selectedOperatorList.getSelected(
        details['ASEAN CEPT Duty Operator'],
        'value',
      );

      this.selectedCESSForPlantingAmt = details['Cess for Planting Rate'];
      this.selectedCESSForResearchAmt = details['Cess for Research Rate'];
      this.selectedCESSRate = details['Cess Rate'];

      this.selectedExciseAmt2 = details['Excise Rate 2'];
      this.selectedExciseType2 = this.selectedTypeList.getSelected(details['Excise Type 2'], 'value');
      this.selectedExciseQualifier2 = this.unitOfQtyData.getSelected(details['Excise Qualifier 2'], 'UnitCode');

      this.selectedExciseOperator = this.selectedOperatorList.getSelected(details['Excise Operator'], 'value');

      this.selectedExciseAmt1 = details['Excise Rate 1'];
      this.selectedExciseType1 = this.selectedTypeList.getSelected(details['Excise Type 1'], 'value');

      this.selectedAlcoholQualifier = this.unitOfQtyData.getSelected(details['Alcohol Qualifier'], 'UnitCode');

      if (!this.hasSubscribed) {
        this.selectedAntiDumpingAmt = details['Anti-Dumping Rate 1'];
        this.selectedAntiDumpingType = this.selectedTypeList.getSelected(details['Anti-Dumping Type 1'], 'value');

        this.selectedAntiDumpingAmt2 = details['Anti-Dumping Rate 2'];
        this.selectedAntiDumpingType2 = this.selectedTypeList.getSelected(details['Anti-Dumping Type 2'], 'value');
        this.selectedAntiDumpingQualifier2 = this.unitOfQtyData.getSelected(
          details['Anti-Dumping Qualifier 2'],
          'UnitCode',
        );

        this.selectedAntiDumpingOperator = this.selectedOperatorList.getSelected(
          details['Anti-Dumping Operator'],
          'value',
        );

        this.selectedADASEANPTAType = this.selectedTypeList.getSelected(details['ASEAN Anti-Dumping Type'], 'value');
        this.selectedADASEANPTAAmt = details['ASEAN Anti-Dumping Rate'];
        this.selectedADASEANPTAQualifier = this.unitOfQtyData.getSelected(
          details['ASEAN Anti-Dumping Qualifier'],
          'UnitCode',
        );

        this.selectedImpASEANPTAType = this.selectedTypeList.getSelected(details['ASEAN PTA Duty Type'], 'value');
        this.selectedImpASEANPTAAmt = details['ASEAN PTA Duty Rate'];
        this.selectedImpASEANPTAQualifier = this.unitOfQtyData.getSelected(
          details['ASEAN PTA Duty Qualifier'],
          'UnitCode',
        );
      } else {
        this.selectedTRQ = details['Tariff Rate Quota'];
        this.selectedImpProhibition = details['Import Prohibition'];
        this.selectedExpProhibition = details['Export Prohibition'];
      }
    },
    resetFormFields() {
      this.selectedImpDutyAmt = null;
      this.selectedImpDutyType = null;
      this.selectedImpDutyQualifier = null;
      this.selectedImpCA1Amt = null;
      this.selectedImpCA2Amt = null;
      this.selectedImpCA1Type = null;
      this.selectedImpCA2Type = null;
      this.selectedImpCO1Amt = null;
      this.selectedImpCO1Type = null;
      this.selectedImpCO2Amt = null;
      this.selectedImpCO2Type = null;
      this.selectedImpASEANPTAType = null;
      this.selectedImpASEANPTAAmt = null;
      this.selectedImpASEANPTAQualifier = null;
      this.selectedExpDutyAmt = null;
      this.selectedExpDutyType = null;
      this.selectedExpDutyQualifier = null;
      this.selectedExpDutyAmt2 = null;
      this.selectedExpDutyType2 = null;
      this.selectedExpDutyQualifier2 = null;
      this.selectedTaxDutyAmt = null;
      this.selectedTaxDutyType = null;
      this.selectedASEANCEPTAmt = null;
      this.selectedASEANCEPTType = null;
      this.selectedCESSForPlantingAmt = null;
      this.selectedCESSForResearchAmt = null;
      this.selectedCESSRate = null;
      this.selectedExciseAmt2 = null;
      this.selectedExciseType2 = null;
      this.selectedExciseOperator = null;
      this.selectedExciseAmt1 = null;
      this.selectedExciseType1 = null;
      this.selectedExciseQualifier2 = null;
      this.selectedAlcoholQualifier = null;
      this.selectedAntiDumpingAmt = null;
      this.selectedAntiDumpingType = null;
      this.selectedAntiDumpingAmt2 = null;
      this.selectedAntiDumpingType2 = null;
      this.selectedAntiDumpingQualifier2 = null;
      this.selectedAntiDumpingOperator = null;
      this.selectedADASEANPTAType = null;
      this.selectedADASEANPTAAmt = null;
      this.selectedADASEANPTAQualifier = null;
    },
    async saveClick() {
      if (this.popUpAction === 'N') {
        let error = false;
        if (this.selectedTCDesc === null || this.selectedTCDesc === '') {
          this.tcDescErr = 'Fill in missing field';
          error = true;
        }
        if (this.selectedTCNumber === null || this.selectedTCNumber === '') {
          this.tcNumberErr = 'Fill in missing field';
          error = true;
        }

        const inputTCNumber = this.selectedTCNumber.replace(/[^\d]/g, '');
        const findFunction =
          this.hasSubscribed ?
            (tariffCode) => tariffCode.TcNumber === inputTCNumber
          : (tariffCode) => tariffCode.TcNumber.replace(/[^\d]/g, '') === inputTCNumber;
        const duplicate = this.tariffCodeSummaryData.some(findFunction);
        if (duplicate) {
          this.tcNumberErr = 'Record already exists';
          error = true;
        }

        if (error) {
          return;
        }
      }

      const jsonBody = {
        tcnnumber: this.selectedTCNumber,
        tcdesc: this.selectedTCDesc,
        tcunit: this.selectedTCUnitText,

        tcimpdutytype: this.selectedImpDutyType?.value ?? null,
        tcimpdutyamt: this.selectedImpDutyAmt,
        tcimpdutyqualifier: this.selectedImpDutyQualifier?.UnitCode ?? null,

        tcimpca1type: this.selectedImpCA1Type?.value ?? null,
        tcimpca1amt: this.selectedImpCA1Amt,

        tcimpca2type: this.selectedImpCA2Type?.value ?? null,
        tcimpca2amt: this.selectedImpCA2Amt,

        tcimpco1type: this.selectedImpCO1Type?.value ?? null,
        tcimpco1amt: this.selectedImpCO1Amt,

        tcimpco2type: this.selectedImpCO2Type?.value ?? null,
        tcimpco2amt: this.selectedImpCO2Amt,

        tcaseanptatype: this.selectedImpASEANPTAType?.value ?? null,
        tcaseanptaamt: this.selectedImpASEANPTAAmt,
        tcaseanptaqualifier: this.selectedImpASEANPTAQualifier?.UnitCode ?? null,

        tcexpdutytype: this.selectedExpDutyType?.value ?? null,
        tcexpdutyamt: this.selectedExpDutyAmt,
        tcexpdutyqualifier: this.selectedExpDutyQualifier?.UnitCode ?? null,

        tcexpdutytype2: this.selectedExpDutyType2?.value ?? null,
        tcexpdutyamt2: this.selectedExpDutyAmt2,
        tcexpdutyqualifier2: this.selectedExpDutyQualifier2?.UnitCode ?? null,

        tctaxdutytype: this.selectedTaxDutyType?.value ?? null,
        tctaxdutyamt: this.selectedTaxDutyAmt,
        tctaxdutyqualifier: this.selectedTaxDutyQualifier?.UnitCode ?? null,

        tcaseancepttype: this.selectedASEANCEPTType?.value ?? null,
        tcaseanceptamt: this.selectedASEANCEPTAmt,
        tcaseanceptqualifier: this.selectedASEANCEPTQualifier?.UnitCode ?? null,

        tcaseanceptamt1: this.selectedASEANCEPTAmt2,
        tcaseancepttype1: this.selectedASEANCEPTType2?.value ?? null,
        tcaseanceptqualifier1: this.selectedASEANCEPTQualifier2?.UnitCode ?? null,
        tcaseanceptoperator: this.selectedASEANCEPTOperator?.value ?? null,

        excisetype: this.selectedExciseType1?.value ?? null,
        exciseamt: this.selectedExciseAmt1,
        exciseoperator: this.selectedExciseOperator?.value ?? null,

        excisetype1: this.selectedExciseType2?.value ?? null,
        exciseamt1: this.selectedExciseAmt2,
        excisequalifier1: this.selectedExciseQualifier2?.UnitCode ?? null,

        cessrate: Number(this.selectedCESSRate),
        cessforplantingamt: this.selectedCESSForPlantingAmt,
        cessforresearchamt: this.selectedCESSForResearchAmt,

        alcoholqualifier: this.selectedAlcoholQualifier?.UnitCode ?? null,

        antidumpingtype: this.selectedAntiDumpingType?.value ?? null,
        antidumpingamt: this.selectedAntiDumpingAmt,

        antidumpingtype1: this.selectedAntiDumpingType2?.value ?? null,
        antidumpingamt1: this.selectedAntiDumpingAmt2,
        antidumpingqualifier1: this.selectedAntiDumpingQualifier2?.UnitCode ?? null,
        antidumpingoperator: this.selectedAntiDumpingOperator?.value ?? null,

        aseanadtype: this.selectedADASEANPTAType?.value ?? null,
        aseanadamt: this.selectedADASEANPTAAmt,
        aseanadqualifier: this.selectedADASEANPTAQualifier?.UnitCode ?? null,

        actionflag: this.popUpAction,
      };
      await this.$apiCallV2('/SMKTariffCode/SaveSMKTariffCode', jsonBody);
      this.viewMode = true;
      this.popUpTitle = 'View SMK Tariff Code';
      this.popUpAction = 'V';
      this.reloadTableFunc();
    },
    updateTariffTypes(value) {
      this.tariffTypesErrMsg = '';
      this.selectedTariffTypes = value;
    },
    async refreshClicked() {
      if(this.selectedTariffTypes.length === 0){ //Early return if no data passed
        this.tariffTypesErrMsg = 'Tariff Type is required!';
        return;
      }
      this.tariffTypesErrMsg = '';
      this.filterText = `Filter by ${this.selectedTariffTypes.map((tariffType) => tariffType['Tariff Type Desc'])}`;
      this.mainFilterIsOpen = false;
      const filterItems = this.selectedTariffTypes.map((tariffType) => tariffType['Tariff Type']).join(',');
      this.tableLoading = true;
      this.tariffCodeSummaryData = [];
      this.tariffCodeSummaryData = await this.$apiCallV2('/SMKTariffCode/GetSMKTariffCodeMaster', {
        tarifftypes: filterItems,
      });
      this.reloadTableFunc();
      this.columnOrderViews();
      this.tableLoading = false;
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
    },
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=MASTER&page=SMK Tariff Code`);

      sasControl.forEach((k) => {
        //key in menu same as control value from api
        const selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
        if (selectedMenu) {
          selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
        }
      });
      this.$fullscreenLoading('');
    },
  },
  async mounted() {
    await this.sasControlFunction();

    const mountedPromises = [];
    let mountedPromisesFulfilledCount = 0;
    let getAPICheck = null;
    this.tableLoading = true;
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SMKUNITOFQTY' }).then((result) => {
        this.unitOfQtyData = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=MASTER_SMKTC_API').then((result) => {
        getAPICheck = result?.[0]['SettingsValue'];
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SMKTARIFFTYPES' }).then((result) => {
        this.tariffTypeList = result;
        this.tariffTypeList.forEach(
          (tariffCode) =>
            (tariffCode['Tariff Description'] = `${tariffCode['Tariff Type']} - ${tariffCode['Tariff Type Desc']}`),
        );
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises);
    if (getAPICheck && getAPICheck === '1') {
      this.menus[0].visible = false; //Disable create, edit and delete
      this.menus[1].visible = false;
      this.menus[2].visible = false;
      this.updateTariffTypes([this.tariffTypeList[0]]);
      this.hasSubscribed = true;
      this.allowFreeText = true; // For units that is not in the drop down list for unitofqtydata
      this.hideColumns = ['File Name', 'User ID', 'Tariff Type'];
      this.filterHideColumn = ['Tariff Type', 'Tariff Type Desc'];
      await this.refreshClicked(); // columnOrderViews already in refreshClicked
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
    } else {
      this.tariffCodeSummaryData = await this.$apiCallV2('/SMKTariffCode/GetSMKTariffCodeSummaryDetails', {});
      this.hasSubscribed = false;
      await this.columnOrderViews(); // This is called here
    }
    this.tableLoading = false;
    this.disableFilterButton = false;
    this.menus.forEach((menuOption) => {
      menuOption.enabled = true;
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.smktariffcode-main {
  padding: 5px 45px;
  font-size: 12px;
  display: flex;
  height: 100%;
  > .right-container {
    border-radius: 5px;
    width: 100%;
    margin-left: 5px;
    overflow: hidden;

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      background-color: white;
      border-radius: 5px;
      margin-bottom: 5px;
      padding: 5px;

      > .filter-description {
        padding-right: 8px;
      }

      > .button-filter-component {
        margin-right: 5px;
        position: relative;

        .filter-popup {
          display: flex;
          flex-direction: column;

          > button {
            margin-top: 5px;
            width: fit-content;
            align-self: flex-end;
          }
        }
      }
    }
  }

  .popup-entry-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    > .popup-top-section {
      display: flex;
      flex-direction: column;
    }

    > .smktariffcode-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow-y: auto;
      justify-content: flex-start;
      .smktariffcode-subbody {
        flex-direction: row;
        display: flex;
        gap: 4px;
        height: 100%;
        justify-content: center;
      }
      .boxcontainer-width {
        width: 33%;
      }
      .smktariffcode-field {
        display: flex;
        gap: 8px;
        min-width: 0;

        .label-text {
          flex: 0 0 92px;
          margin: 0;
        }

        .input-amount {
          flex: 0 0 12%;
        }

        .field-type {
          flex: 0 0 18%;
        }

        .field-qualifier {
          flex: 1 1 0;
          min-width: 0;
        }
      }
    }

    > .smktariffcode-body-wait {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
